import { PageDataType } from '@common/paged-data/enums/page-data-type.enum';
import { ProcessLogMessageType } from '@portal-core/processes/enums/process-log-message-type.enum';
import { GridColumn } from '@portal-core/ui/grid/models/grid-column.model';
import { PageFilterConfigBase } from '@portal-core/ui/page-filters/util/page-filter-config-base';

// Single source of truth for the Type column's filter values, display labels, and the
// ProcessLogMessageType they map to. The id is what the grid stores in PropertyValue; the
// label is what the user sees in the filter dropdown and Type column tooltip; the
// logMessageType is what each row carries internally.
export const LogTypeFilter = {
  Error:   { id: 'error',   label: 'Error',   logMessageType: ProcessLogMessageType.Error },
  Warning: { id: 'warning', label: 'Warning', logMessageType: ProcessLogMessageType.Warning },
  Info:    { id: 'info',    label: 'Info',    logMessageType: ProcessLogMessageType.Message },
  Event:   { id: 'event',   label: 'Event',   logMessageType: ProcessLogMessageType.Event }
} as const;

export type LogTypeFilterId = typeof LogTypeFilter[keyof typeof LogTypeFilter]['id'];

// Resolve a Type column filter id back to the legacy ProcessLogMessageType. Returns null for
// unknown ids — the caller treats those as a no-match so a stale/unknown value never silently
// broadens the visible set.
export function logTypeFilterIdToLogMessageType(id: string): ProcessLogMessageType | null {
  return Object.values(LogTypeFilter).find(entry => entry.id === id)?.logMessageType ?? null;
}

// Reverse lookup: enum value → display label, used by the grid's Type column cell template.
export function logTypeFilterLabel(logMessageType: ProcessLogMessageType): string {
  return Object.values(LogTypeFilter).find(entry => entry.logMessageType === logMessageType)?.label ?? '';
}

export class LogPageFilterConfig extends PageFilterConfigBase {
  // Sort is disabled across the board while the data source is the live SSE stream — the
  // entries arrive in stream order and we don't want to thrash them. The Type column gets the
  // standard multi-select column-header filter (mc-grid-select-filter) since it has a known
  // small set of values; the grid component intercepts the resulting PageFilter and applies
  // it client-side over the SSE accumulator.
  static readonly Columns: GridColumn[] = [
    {
      name: 'LogMessageType',
      title: 'Type',
      type: PageDataType.Select,
      filterEnabled: true,
      selectOptions: Object.values(LogTypeFilter).map(entry => ({ id: entry.id, text: entry.label }))
    },
    {
      name: 'Message',
      title: 'Message',
      type: PageDataType.String,
      filterEnabled: true
    },
    {
      name: 'ErrorId',
      title: 'Error ID',
      type: PageDataType.String,
      filterEnabled: true
    },
    {
      name: 'File',
      title: 'File',
      type: PageDataType.String,
      filterEnabled: true
    },
    {
      name: 'Subsystem',
      title: 'Subsystem',
      type: PageDataType.String,
      filterEnabled: true
    }
  ];

  constructor(enabledColumnNames?: string[]) {
    super(enabledColumnNames, LogPageFilterConfig.Columns);
  }
}
